import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { Check, RotateCcw, Pause } from 'lucide-react';
import { DemoStage } from './DemoChrome';
import { DEMO_REVIEW_FRAME_STILLS } from './demoStockImages';
import './marketingDemos.css';

const NOTES = [
  {
    time: '0:42',
    seconds: 42,
    text: 'Phrasing here is a bit weak. Can you tighten it up?',
    author: 'Alex (Producer)',
    poster: DEMO_REVIEW_FRAME_STILLS[0],
  },
  {
    time: '1:24',
    seconds: 84,
    text: 'Trim this part before client review',
    author: 'Jordan (Editor)',
    poster: DEMO_REVIEW_FRAME_STILLS[1],
  },
  {
    time: '2:08',
    seconds: 128,
    text: 'Add logo end card here. Please make sure we make the CTA more prominent.',
    author: 'Alex (Producer)',
    poster: DEMO_REVIEW_FRAME_STILLS[2],
  },
];

const TOTAL_DURATION_SECONDS = 150;

function formatNoteTimestamp(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `@${mins}:${String(secs).padStart(2, '0')}`;
}

function wait(durationMs) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, durationMs);
  });
}

export default function ReviewPlayerDemo() {
  const reduceMotion = useReducedMotion();
  const rootRef = useRef(null);
  const notesListRef = useRef(null);
  const isInView = useInView(rootRef, { amount: 0.3, once: false });
  const [step, setStep] = useState('play');
  const [noteIndex, setNoteIndex] = useState(0);
  const [typedChars, setTypedChars] = useState(0);
  const [postedNoteIndices, setPostedNoteIndices] = useState([]);

  useEffect(() => {
    DEMO_REVIEW_FRAME_STILLS.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if (!isInView) {
      return undefined;
    }

    let cancelled = false;

    const runNoteCycle = async (index) => {
      if (cancelled) return;

      setNoteIndex(index);
      setStep('play');
      setTypedChars(0);
      await wait(reduceMotion ? 500 : 850);

      if (cancelled) return;
      setStep('insert');
      await wait(reduceMotion ? 400 : 650);

      if (cancelled) return;
      setStep('type');
      const noteText = NOTES[index].text;
      const charDelayMs = reduceMotion ? 0 : 42;

      for (let charCount = 0; charCount <= noteText.length; charCount += 1) {
        if (cancelled) return;
        setTypedChars(charCount);
        if (charCount < noteText.length) {
          await wait(charDelayMs);
        }
      }

      if (cancelled) return;
      setStep('send');
      await wait(reduceMotion ? 350 : 550);

      if (cancelled) return;
      setPostedNoteIndices((previous) =>
        previous.includes(index) ? previous : [...previous, index],
      );
      setStep('idle');
      setTypedChars(0);
      await wait(reduceMotion ? 450 : 700);
    };

    const runLoop = async () => {
      while (!cancelled) {
        setPostedNoteIndices([]);
        setNoteIndex(0);
        setTypedChars(0);
        setStep('play');

        for (let index = 0; index < NOTES.length; index += 1) {
          await runNoteCycle(index);
        }

        if (cancelled) return;
        await wait(reduceMotion ? 500 : 900);
      }
    };

    runLoop();

    return () => {
      cancelled = true;
    };
  }, [isInView, reduceMotion]);

  useEffect(() => {
    const notesListElement = notesListRef.current;
    if (!notesListElement) {
      return;
    }
    notesListElement.scrollTop = notesListElement.scrollHeight;
  }, [postedNoteIndices]);

  const isPlaying = step === 'play';
  const isInsertStep = step === 'insert';
  const isTypingStep = step === 'type';
  const isSendStep = step === 'send';
  const isComposeActive = isInsertStep || isTypingStep || isSendStep;
  const activeNote = NOTES[noteIndex];
  const progressSeconds = isPlaying
    ? Math.max(0, activeNote.seconds - 12)
    : activeNote.seconds;
  const progressPercent = `${Math.min(100, (progressSeconds / TOTAL_DURATION_SECONDS) * 100)}%`;

  const composePrefix = isComposeActive ? `${formatNoteTimestamp(activeNote.seconds)} ` : '';
  const composeBody =
    isTypingStep || isSendStep
      ? activeNote.text.slice(0, isSendStep ? activeNote.text.length : typedChars)
      : '';
  const composePlaceholder = 'Add a note at the current playback time…';

  return (
    <div ref={rootRef}>
      <DemoStage
        className="mkt-demo-stage--review-visible"
        visualClassName="mkt-demo-visual--review"
      >
        <div className="mkt-review-modal-shell">
          <div className="mkt-review-modal-header">
            <h3 className="mkt-review-modal-title">Review media</h3>
            <span className="mkt-review-modal-filename">Brand interview.mp4</span>
          </div>

          <div className="mkt-review-modal-body">
            <div className="mkt-review-modal-media">
              <div className="mkt-review-screen mkt-review-screen--modal" aria-hidden>
                <img
                  key={activeNote.poster}
                  src={activeNote.poster}
                  alt=""
                  className="mkt-review-screen-poster mkt-review-screen-poster--active"
                  loading="eager"
                  decoding="async"
                />
                <div className="mkt-review-screen-scrim" aria-hidden />
                {isPlaying && !reduceMotion ? (
                  <motion.div
                    className="mkt-review-screen-scan"
                    animate={{ x: ['-100%', '200%'] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
                  />
                ) : null}
                {!isPlaying ? (
                  <div className="mkt-review-screen-paused" aria-hidden>
                    <Pause className="ui-icon-xl" />
                  </div>
                ) : null}
                <button
                  type="button"
                  className={`mkt-review-insert-time-btn${
                    isInsertStep ? ' mkt-review-insert-time-btn--highlight' : ''
                  }`}
                  tabIndex={-1}
                >
                  Insert time in note
                </button>
              </div>
              <div className="mkt-review-bar-wrap">
                <div className="mkt-review-bar">
                  <div className="mkt-review-bar-fill" style={{ width: progressPercent }} />
                  {NOTES.map((note, index) => (
                    <span
                      key={note.time}
                      className={`mkt-review-marker-btn mkt-review-marker-btn--auto${
                        noteIndex === index && !isPlaying ? ' mkt-review-marker-btn--active' : ''
                      }${postedNoteIndices.includes(index) ? ' mkt-review-marker-btn--posted' : ''}`}
                      style={{ left: `${(note.seconds / TOTAL_DURATION_SECONDS) * 100}%` }}
                      aria-hidden
                    />
                  ))}
                </div>
              </div>
            </div>

            <aside className="mkt-review-modal-panel">
              <div className="mkt-review-modal-tabs" aria-hidden>
                <span className="mkt-review-modal-tab mkt-review-modal-tab--active">Internal</span>
                <span className="mkt-review-modal-tab">Client</span>
              </div>

              <div className="mkt-review-modal-notes" ref={notesListRef}>
                {postedNoteIndices.length === 0 ? (
                  <p className="mkt-review-modal-notes-empty">Notes appear here as reviewers add them.</p>
                ) : null}
                {NOTES.filter((_, index) => postedNoteIndices.includes(index)).map((note) => (
                  <div key={note.time} className="mkt-review-modal-note">
                    <div className="mkt-review-modal-note-head">
                      <span className="mkt-review-modal-note-author">{note.author}</span>
                    </div>
                    <p className="mkt-review-modal-note-body">
                      <span className="mkt-review-compose-prefix">
                        {formatNoteTimestamp(note.seconds)}
                      </span>
                      {' '}
                      {note.text}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mkt-review-modal-compose">
                <label className="mkt-review-modal-compose-label">Add feedback</label>
                <div
                  className={`mkt-review-modal-compose-field${
                    isComposeActive ? ' mkt-review-modal-compose-field--active' : ''
                  }${isTypingStep || isSendStep ? ' mkt-review-modal-compose-field--typing' : ''}`}
                >
                  {isComposeActive ? (
                    <>
                      <span className="mkt-review-compose-prefix">{composePrefix}</span>
                      <span className="mkt-review-compose-body">{composeBody}</span>
                      {isTypingStep && typedChars < activeNote.text.length ? (
                        <span className="mkt-review-compose-cursor" aria-hidden />
                      ) : null}
                    </>
                  ) : (
                    composePlaceholder
                  )}
                </div>
                <div className="mkt-review-modal-compose-actions">
                  <span
                    className={`mkt-review-modal-compose-hint${
                      isInsertStep ? ' mkt-review-modal-compose-hint--highlight' : ''
                    }`}
                  >
                    Insert time in note
                  </span>
                  <button
                    type="button"
                    className={`mkt-review-compose-send-btn${
                      isSendStep ? ' mkt-review-compose-send-btn--highlight' : ''
                    }`}
                    tabIndex={-1}
                  >
                    Send note
                  </button>
                </div>
              </div>

              <div className="mkt-review-modal-actions">
                <div className="mkt-review-btn mkt-review-btn--approve mkt-review-btn--auto">
                  <Check className="ui-icon-md" aria-hidden />
                  Approve version
                </div>
                <div className="mkt-review-btn mkt-review-btn--changes mkt-review-btn--auto">
                  <RotateCcw className="ui-icon-md" aria-hidden />
                  Request changes
                </div>
              </div>
            </aside>
          </div>
        </div>
      </DemoStage>
    </div>
  );
}
